import {
  selectOrganizationId,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import type {
  Activity,
  IDemo,
  IPresentation,
  IRecording,
  IUser,
  Revision,
  Target,
} from '@carboncopyco/types';
import { Spacer, TextBase, notify } from '@carboncopyco/ui';
import type React from 'react';
import { useSelector } from 'react-redux';
import { useWorkspacesMap } from '../../../hooks/useWorkspacesMap';
import { ActivityItemUpdateTitle } from './ActivityItemUpdateTitle';
import ActivityListItem, { type ExternalDescription } from './ActivityListItem';

export const ACTIVITIES_ACTIONS = {
  JOINED: 'joined',
  CREATED: 'created',
  UPDATED: 'updated',
  SETTINGS_CHANGED: 'settings_changed',
  REVERTED: 'reverted',
  IMPORTED: 'imported',
  STARTED: 'started',
};

interface ActivityManagerProps {
  data: Activity<IRecording | IDemo | IPresentation | IUser>;
  goToDemoPageByWorkspace: (
    demoId: number,
    workspaceId: string,
    tab?: string | undefined
  ) => void;
  goToClonePageByWorkspace: (
    id: any,
    workspaceId: string,
    tab?: string | undefined
  ) => void;
  goToPresentationPageFromHomepage: (
    id: any,
    workspaceId: any,
    tab?: string | undefined
  ) => void;
}

interface ActivityItemParams {
  title: string | React.ReactNode;
  externalDescription?: ExternalDescription;
}

const ActivityManager = ({
  data: activityItem,
  goToDemoPageByWorkspace,
  goToClonePageByWorkspace,
}: ActivityManagerProps) => {
  const workspacesMap = useWorkspacesMap();

  const organizationId = useSelector(selectOrganizationId);
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const organizationName = organization?.organizationName;
  const { target, action, creator, createTime, id } = activityItem;

  const displayExtraText = (revertedFrom: Revision) => {
    return Array.isArray(revertedFrom)
      ? `back to revision: "${revertedFrom[0].currRevision + 1}"`
      : `back to revision: "1"`;
  };
  const getDemoItemParams = () => {
    const { data } = activityItem as Activity<IDemo>;
    const { demo, draft, revertedFrom } = data;

    if (!demo) {
      return null;
    }

    const {
      demoName: title,
      image,
      id: demoId,
      description,
      workspaceId,
    } = demo;
    const onClick = () => {
      goToDemoPageByWorkspace(demoId, workspaceId);
    };
    let titleParams: {
      text: string | React.ReactNode;
      externalText?: string | React.ReactNode;
    } | null = null;

    switch (action) {
      case ACTIVITIES_ACTIONS.UPDATED:
        titleParams = {
          text: action,
          externalText: draft?.comment && `"${draft.comment || ''}"`,
        };
        break;

      case ACTIVITIES_ACTIONS.REVERTED:
        titleParams = {
          text: action,
          externalText: displayExtraText(revertedFrom as Revision),
        };

        break;

      case ACTIVITIES_ACTIONS.CREATED:
        titleParams = {
          text: (
            <TextBase
              label={`${action} a new demo:`}
              color="text-ds-natural-600"
            />
          ),
        };
        break;

      case ACTIVITIES_ACTIONS.SETTINGS_CHANGED:
        titleParams = {
          text: (
            <TextBase
              label="updated the settings for"
              color="text-ds-natural-600"
            />
          ),
        };
        break;

      default:
        return null;
    }

    return {
      title: (
        <ActivityItemUpdateTitle
          onClick={onClick}
          title={title}
          creator={creator}
          {...titleParams}
        />
      ),
      externalDescription: { title, image, description },
      workspaceName: workspacesMap[workspaceId]?.workspaceName,
    };
  };

  const getRecordingParams = () => {
    const { data } = activityItem as Activity<IRecording>;
    const { recording, draft } = data;

    if (!recording) {
      return null;
    }

    const {
      recordName: title,
      image,
      id: recordingId,
      workspaceId,
    } = recording;
    let titleParams: {
      text: string | React.ReactNode;
      externalText?: string | React.ReactNode;
    } | null = null;

    switch (action) {
      case ACTIVITIES_ACTIONS.IMPORTED:
        titleParams = {
          text: 'added a new product clone:',
        };
        break;

      case ACTIVITIES_ACTIONS.UPDATED:
        titleParams = {
          text: 'updated',
          externalText: draft?.comment && `"${draft.comment || ''}"`,
        };
        break;

      default:
        return null;
    }

    return {
      title: (
        <ActivityItemUpdateTitle
          onClick={() => {
            goToClonePageByWorkspace(recordingId, workspaceId);
          }}
          title={title}
          creator={creator}
          {...titleParams}
        />
      ),
      externalDescription: { title, image },
      workspaceName: workspacesMap[workspaceId]?.workspaceName,
    };
  };

  const getMemberParams = () => {
    const {
      data: {
        user: { firstName, lastName },
      },
    } = activityItem as any;

    if (action === ACTIVITIES_ACTIONS.JOINED) {
      return {
        title: (
          <div className="flex items-center">
            <TextBase
              label={`${firstName} ${lastName} joined your team on ${organizationName}. Welcome aboard ${firstName}.`}
              color="text-ds-natural-600"
            />
            <Spacer amount={0.5} />
          </div>
        ),
      };
    }
    return;
  };

  const getPresentationParams = () => {
    const { data } = activityItem as Activity<IPresentation>;
    const { presentation, demoName: title } = data;

    if (!presentation || !title) {
      return null;
    }

    const { image, forClient, workspaceId } = presentation;

    let titleParams: {
      text: string;
      externalText?: string | React.ReactNode;
    } | null = null;

    switch (action) {
      case ACTIVITIES_ACTIONS.STARTED:
        titleParams = {
          text: 'presented',
          externalText: (
            <TextBase
              color="text-ds-primary"
              label={`to ${forClient}. Good luck ${creator.firstName} ${creator.lastName}!`}
            />
          ),
        };
        break;

      default:
        return null;
    }

    const onActivityClick = () =>
      goToDemoPageByWorkspace(presentation.demoId, workspaceId, 'overview');

    return {
      title: (
        <ActivityItemUpdateTitle
          onClick={onActivityClick}
          title={title}
          creator={creator}
          {...titleParams}
        />
      ),
      externalDescription: { title, image },
      workspaceName: workspacesMap[workspaceId]?.workspaceName,
    };
  };

  const getActivityItemParams: () => ActivityItemParams | null = () => {
    try {
      const targetToGetParams: Partial<Record<Target, () => any>> = {
        demo: getDemoItemParams,
        recording: getRecordingParams,
        presentation: getPresentationParams,
        member: getMemberParams,
      };

      return targetToGetParams[target]?.() ?? null;
    } catch (_error) {
      notify.error('Error restoring clone');
      return null;
    }
  };

  const activityParams = getActivityItemParams();

  if (!activityParams) {
    return null;
  }

  const handleIsOverlayDemo = () => {
    const { data } = activityItem as Activity<IDemo | IPresentation>;
    if (target === 'presentation') {
      const { presentation } = data;
      return presentation?.recordingType === 'live_demo';
    }
    if (target === 'demo') {
      const { demo } = data;
      return demo?.recordingType === 'live_demo';
    }
    return;
  };
  return (
    <ActivityListItem
      key={id}
      creator={creator}
      createTime={createTime}
      isOverlayDemo={handleIsOverlayDemo()}
      {...activityParams}
    />
  );
};

export default ActivityManager;
